import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {Body} from 'shells/typography/body';

import type {ChatAssignmentStrategyEnum, ThreadType} from 'nutshell-graphql-types';

import {getMessageTypeString} from './helpers';

type Props = {
    selectedValue: ChatAssignmentStrategyEnum;
    assignedUserId: string | null | undefined;
    setSelectedValue: (value: ChatAssignmentStrategyEnum) => void;
    handleSaveAssignRules: (
        value: ChatAssignmentStrategyEnum,
        assigneeId?: string | null | undefined
    ) => void;
    threadType: ThreadType;
    hasNewColumnLayout?: boolean;
};

export function AssignmentRules(props: Props) {
    const assignables = useSelector(Assignables.getAgents);

    const messageTypeString = getMessageTypeString(props.threadType);

    return (
        <div
            className={`flex flex-dir-col ${props.hasNewColumnLayout ? '' : 'border br-3 pad-16'}`}
        >
            <StackLegacy spacing={16}>
                {!props.hasNewColumnLayout && (
                    <>
                        <Body>
                            Set the rules for automatically assigning first-time inbound{' '}
                            {messageTypeString} messages. This configuration determines who will
                            handle new incoming {messageTypeString} messages to ensure timely
                            responses and efficient follow-up.
                        </Body>
                        <Body>Assign messages to…</Body>
                    </>
                )}
                <RadioGroup
                    name='vertical'
                    selectedValue={props.selectedValue}
                    onChange={(value) => {
                        // We do not want to save when the user selects "Specific user"
                        // because the assignee is set separately
                        if (value === 'SPECIFIC_USER') {
                            props.setSelectedValue('SPECIFIC_USER');

                            return;
                        }
                        // @ts-expect-error
                        props.handleSaveAssignRules(value, null);
                    }}
                    spacing={16}
                >
                    <Radio shouldTopAlignInput={true} value='ROUND_ROBIN'>
                        <StackLegacy spacing={6}>
                            <Body isBold={true}>Round-robin</Body>
                            <Body color='light'>
                                Inbound messages will be distributed evenly among your team.
                            </Body>
                        </StackLegacy>
                    </Radio>
                    <Radio
                        disabled={props.selectedValue === 'SPECIFIC_USER'}
                        shouldTopAlignInput={true}
                        value='SPECIFIC_USER'
                    >
                        <StackLegacy spacing={6}>
                            <Body isBold={true}>Specific-user</Body>
                            <Body color='light'>
                                All inbound messages will be assigned to a specific user.
                            </Body>
                            {props.selectedValue === 'SPECIFIC_USER' && (
                                <div className='my-8'>
                                    <AssigneeSelect
                                        value={props.assignedUserId}
                                        // @ts-expect-error
                                        options={assignables}
                                        isClearable={false}
                                        onChange={(value) =>
                                            props.handleSaveAssignRules(props.selectedValue, value)
                                        }
                                        minHeight={34}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                            )}
                        </StackLegacy>
                    </Radio>
                    <Radio shouldTopAlignInput={true} value='NOBODY'>
                        <StackLegacy spacing={6}>
                            <Body isBold={true}>Nobody</Body>
                            <Body color='light'>
                                Inbound messages won’t be assigned, allowing your team to assign
                                themselves accordingly.
                            </Body>
                        </StackLegacy>
                    </Radio>
                </RadioGroup>
            </StackLegacy>
        </div>
    );
}
